import type { ReactNode } from "react";
import { AgentRecord } from "@/types/chat/agent";
import type { Agent } from "@/types/agent-types";
import { AgentIcon } from "../agents/agent-icon";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowUpLeft } from "lucide-react";
import { cn } from "../../lib/utils";
import teammateLogo from "@/assets/images/logo-icon.svg";
import { isDesktopApp } from "@/lib/desktop";
import { useCoworkStore } from "@/store/cowork-store";
import { usePublicEmbedConfig } from "@/hooks/use-public-embed-config";
import {
  SHOW_SLIDE_TEMPLATE_PICKER,
  SlideTemplatePicker,
} from "./slide-template-picker";
import {
  getChromeStrings,
  isEmbedSurface,
  resolveLocalized,
  useEmbedLocale,
} from "@/lib/embed-locale";

interface ChatEmptyStateProps {
  agentData: AgentRecord | null;
  teamInfo: {
    name: string;
    description: string | null;
    icon: string | null;
    samplePrompts?: string[];
    welcomeMessage?: string | null;
  } | null;
  isCompact: boolean;
  onSuggestionClick: (prompt: string) => void;
  specialistAgents?: Agent[];
  specialistsLoading?: boolean;
  activeSpecialistId?: string | null;
  onSpecialistSelect?: (agent: Agent | null) => void;
  children?: ReactNode;
}

/**
 * Empty state hero content shown when a chat has no messages.
 * Displays agent/team avatar, welcome heading, subtitle, and sample prompt suggestions.
 * Accepts children (e.g. the chat input) to render between the subtitle and suggestions.
 */
export function ChatEmptyState({
  agentData,
  teamInfo,
  isCompact,
  onSuggestionClick,
  specialistAgents = [],
  specialistsLoading = false,
  activeSpecialistId = null,
  onSpecialistSelect,
  children,
}: ChatEmptyStateProps) {
  /**
   * Embedded-widget localization (spec 075 — Phase 3): on the embed surface,
   * prefer the PUBLISHED per-locale starters/welcome from the public embed
   * config; legacy flat fields remain the fallback for every other case.
   */
  const onEmbedSurface = isEmbedSurface();
  const embedLocale = useEmbedLocale();
  const { config: embedConfig } = usePublicEmbedConfig(
    agentData?.id ?? null,
    onEmbedSurface && Boolean(agentData?.id)
  );
  const chrome = getChromeStrings(onEmbedSurface ? embedLocale : null);
  const localizedPrompts = onEmbedSurface
    ? resolveLocalized(embedConfig?.embed?.samplePrompts, embedLocale)
    : undefined;
  const localizedWelcome = onEmbedSurface
    ? resolveLocalized(embedConfig?.embed?.welcomeMessage, embedLocale)
    : undefined;

  const agentSamplePrompts = (agentData?.agentSettings as { samplePrompts?: string[] } | null)?.samplePrompts || [];
  const samplePrompts =
    localizedPrompts && localizedPrompts.length > 0
      ? localizedPrompts
      : agentSamplePrompts.length > 0
        ? agentSamplePrompts
        : (teamInfo?.samplePrompts || []);
  // Hide the specialist picker in CoWork mode (desktop) — CoWork is folder-first.
  const coworkMode = useCoworkStore((s) => s.mode);
  const hideSpecialists = isDesktopApp() && coworkMode === "cowork";
  const showSpecialists = !hideSpecialists && !agentData && !teamInfo && !isCompact && specialistAgents.length > 0;
  const showSpecialistsSkeleton = !hideSpecialists && !agentData && !teamInfo && !isCompact && specialistsLoading && specialistAgents.length === 0;

  return (
    <>
      {/* Agent Avatar - Only show when agentData is present */}
      {agentData && (
        <div className="mb-4 flex items-center justify-center">
          <div className="w-20 h-20 rounded-full overflow-hidden">
            <AgentIcon
              avatar={agentData.avatar}
              name={agentData.name}
              size="xl"
              className="w-20 h-20"
            />
          </div>
        </div>
      )}

      {/* Team Icon - Only show when teamInfo is present and no agent */}
      {!agentData && teamInfo && (
        <div className="mb-4 flex items-center justify-center">
          <div className="w-20 h-20 rounded-full overflow-hidden">
            <AgentIcon
              avatar={teamInfo.icon}
              name={teamInfo.name}
              size="xl"
              className="w-20 h-20"
              rounded="full"
            />
          </div>
        </div>
      )}

      {/* Default TeamMate icon — generic "What can I do for you?" state
          (no specific agent or team). The mark reads on both light and dark
          backgrounds, framed in a subtle adaptive chip for consistency. */}
      {!agentData && !teamInfo && (
        <div className="mb-4 flex items-center justify-center">
          <div className="flex items-center justify-center rounded-2xl bg-muted/50 dark:bg-muted/30 ring-1 ring-border/60 p-3">
            <img
              src={teammateLogo}
              alt="TeamMate"
              className={cn(
                "object-contain select-none",
                isCompact ? "h-10 w-10" : "h-12 w-12"
              )}
            />
          </div>
        </div>
      )}

      {/* Main Heading */}
      <div className="w-full text-center mb-1">
        <h2 className={cn(
          "font-heading text-foreground leading-tight",
          isCompact ? "text-lg font-semibold" : "text-[22px] font-medium"
        )}>
          {agentData?.name ? chrome.welcomeTo(agentData.name) : teamInfo?.name ? chrome.welcomeTo(teamInfo.name) : "What can I do for you?"}
        </h2>
      </div>

      {/* Subtitle */}
      {!isCompact && (
        <p className="text-muted-foreground text-center text-sm mb-4 max-w-md mx-auto">
          {localizedWelcome
            ? localizedWelcome
            : samplePrompts.length > 0
              ? chrome.askAnythingStarters
              : (agentData?.welcomeMessage || teamInfo?.welcomeMessage || chrome.askAnything)}
        </p>
      )}

      {children}

      {showSpecialistsSkeleton && (
        <div className="mt-5 w-full max-w-[760px]">
          <div className="mb-3">
            <Skeleton className="h-4 w-40 mb-1" />
            <Skeleton className="h-3 w-64" />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={`specialist-skeleton-${i}`} className="flex items-start gap-3 rounded-xl border border-border p-3">
                <Skeleton className="h-10 w-10 shrink-0 rounded-lg" />
                <div className="min-w-0 flex-1">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-3/4 mt-1" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showSpecialists && (
        <div className="mt-5 w-full max-w-[760px]">
          <div className="mb-3">
            <p className="text-sm font-medium text-foreground">Choose a specialist</p>
            <p className="text-xs text-muted-foreground">
              Pick a specialist below to start the conversation.
            </p>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            {specialistAgents.map((agent) => {
              const isActive = activeSpecialistId === agent.id;

              return (
                <button
                  key={agent.id}
                  type="button"
                  onClick={() => onSpecialistSelect?.(agent)}
                  className={cn(
                    "flex items-start gap-3 rounded-xl border p-3 text-left transition-colors",
                    isActive
                      ? "border-primary/40 bg-primary/5"
                      : "border-border hover:border-primary/30 hover:bg-muted/30"
                  )}
                >
                  <AgentIcon
                    avatar={agent.avatar}
                    name={agent.name}
                    size="md"
                    rounded="lg"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground">{agent.name}</div>
                    <div className="mt-1 text-xs text-muted-foreground line-clamp-2">
                      {agent.description || "Use this specialist directly for focused support."}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

        </div>
      )}

      {/* Deck-structure picker — TESTING ONLY, hidden by flipping
          SHOW_SLIDE_TEMPLATE_PICKER. v1 selects the structure automatically. */}
      {SHOW_SLIDE_TEMPLATE_PICKER && !isCompact && !agentData && (
        <div className="w-full max-w-[700px] mt-6">
          <SlideTemplatePicker onPick={onSuggestionClick} />
        </div>
      )}

      {/* Suggestion List - Full-width rows with agent-specific prompts */}
      {!isCompact && samplePrompts.length > 0 && (
        <div className="w-full max-w-[700px] mt-6 divide-y divide-border">
          {samplePrompts.map((suggestion: string) => (
            <button
              key={suggestion}
              onClick={() => onSuggestionClick(suggestion)}
              className="w-full flex items-center justify-between py-4 px-2 text-left text-muted-foreground hover:text-foreground transition-colors group"
            >
              <span className="text-sm truncate">{suggestion}</span>
              <ArrowUpLeft className="size-4 opacity-50 group-hover:opacity-100 flex-shrink-0 ms-4 rtl:-scale-x-100" />
            </button>
          ))}
        </div>
      )}
    </>
  );
}
